/**
 * 新增供应商：从预置目录选一家，填 Key 就上线（对应后端 channel_presets）。
 *
 * 背景：原来「新增供应商」直接打开完整表单，渠道 ID、协议、Base URL、节点参数都要手填。创始人 2026-11-03 要求照
 *   Asterove Gateway 的做法：目录预置常用上游，运营只填 Key；后端先探测连通，通过才建渠道、启用并登记上游模型清单。
 * 设计意图：
 *   - 两步：先在目录里按分类或搜索选一家，再填 Key。渠道 ID 默认取目录 ID，已被占用时自动加序号，免得第二把 Key 撞车。
 *   - 目录里没有的上游走「自定义供应商」，回到原来的完整表单（ChannelForm），能力不丢。
 *   - 节点参数由后端按这家上游的特点预填，这里只列出来告诉运营，不让在这一步改；要改去「完整设置」。
 * 关键约束：Key 输入框用 type="text"（仓库约定），安装成功或关闭弹窗后立即清空；有输入框，所以只能按关闭或 Esc 退出
 *   （Modal 默认不响应点遮罩）。安装是一个请求，失败时后端什么都不保存，页面只显示原因。
 */
import { IconArrowLeft, IconExternalLink, IconSettings } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { catalogApi, type ChannelPreset, type PresetCategory, type PresetInstallResult } from '@ap/api-client/admin-catalog'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import { Button, Field, Form, Input, Modal, Notice, Tabs, useLoad } from '@ap/ui'
import { SearchBox } from '../../alice/kit-catalog.tsx'
import './presets.css'

type Tab = 'all' | PresetCategory

/** 节点参数里和默认值不同的项，用来告诉运营这家上游预填了什么。 */
function presetFlags(p: ChannelPreset): string[] {
  const o = p.options
  const out: string[] = []
  if (!o.stream_usage) out.push('stream_usage=false')
  if (o.inject_reasoning_content) out.push('reasoning_content')
  if (o.max_completion_tokens) out.push('max_completion_tokens')
  if (o.retry_statuses.length) out.push(`retry ${o.retry_statuses.join('/')}`)
  if (o.drop_params.length) out.push(`drop ${o.drop_params.join(',')}`)
  if (!o.http2) out.push('http1.1')
  return out
}

/** 目录 ID 没被占用就直接用，否则依次试 -2、-3…，保证默认值可以直接提交。 */
function freeId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base
  for (let n = 2; n < 100; n++) {
    if (!taken.has(`${base}-${n}`)) return `${base}-${n}`
  }
  return ''
}

function PresetIcon({ p }: { p: ChannelPreset }) {
  const [broken, setBroken] = useState(false)
  return (
    <span className="cat-preset-icon" aria-hidden>
      {p.icon_url && !broken ? <img src={p.icon_url} alt="" onError={() => setBroken(true)} /> : p.name.slice(0, 2).toUpperCase()}
    </span>
  )
}

export function PresetPicker({
  open,
  channelIds,
  onClose,
  onInstalled,
  onCustom,
}: {
  open: boolean
  channelIds: string[]
  onClose: () => void
  onInstalled: (r: PresetInstallResult) => void
  onCustom: () => void
}) {
  const { t, locale } = useI18n()
  const presets = useLoad(() => (open ? catalogApi.channelPresets() : Promise.resolve(null)), [open])
  const [tab, setTab] = useState<Tab>('all')
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<ChannelPreset | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [advanced, setAdvanced] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const taken = useMemo(() => new Set(channelIds), [channelIds])
  const items = presets.data?.items ?? []
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter(
      (p) => (tab === 'all' || p.category === tab) && (!q || p.name.toLowerCase().includes(q) || p.id.includes(q) || p.base_url.includes(q)),
    )
  }, [items, tab, search])

  const pick = (p: ChannelPreset) => {
    setPicked(p)
    setApiKey('')
    setId(freeId(p.id, taken))
    setName(p.name)
    setBaseUrl(p.base_url)
    setAdvanced(false)
    setError('')
  }

  const close = () => {
    if (busy) return
    setPicked(null)
    setApiKey('')
    setError('')
    onClose()
  }

  const install = async () => {
    if (!picked) return
    setBusy(true)
    setError('')
    try {
      const r = await catalogApi.installPreset(picked.id, {
        api_key: apiKey.trim(),
        channel_id: id.trim(),
        name: name.trim(),
        ...(baseUrl.trim() && baseUrl.trim() !== picked.base_url ? { base_url: baseUrl.trim() } : {}),
      })
      setApiKey('')
      setPicked(null)
      onInstalled(r)
    } catch (e) {
      console.error('[providers] 预置供应商安装失败', e)
      setError(errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const tabs: { id: Tab; label: string }[] = (['all', 'global', 'china', 'coding', 'platform'] as const).map((c) => ({
    id: c,
    label: t(G.presets.category[c]),
  }))

  return (
    <Modal open={open} onClose={close} busy={busy} wide title={picked ? picked.name : t(G.presets.title)}>
      {!picked ? (
        <div className="ap-stack cat-preset-body" style={{ gap: 12 }}>
          <div className="ap-row ap-row--between" style={{ flexWrap: 'wrap', gap: 8 }}>
            <Tabs items={tabs} value={tab} onChange={setTab} />
            <SearchBox value={search} onChange={setSearch} placeholder={t(G.presets.search)} width={220} />
          </div>
          {presets.error ? (
            <Notice tone="danger">{t(G.common.loadFailed, { msg: errorTextCat(presets.error, locale) })}</Notice>
          ) : presets.loading && !presets.data ? (
            <div className="ap-muted">{t(G.common.loading)}</div>
          ) : shown.length === 0 ? (
            <div className="ap-muted">{t(G.presets.none)}</div>
          ) : (
            <div className="cat-preset-grid">
              {shown.map((p) => (
                <button key={p.id} type="button" className="cat-preset-card" onClick={() => pick(p)}>
                  <PresetIcon p={p} />
                  <span className="cat-preset-main">
                    <span className="cat-preset-name">{p.name}</span>
                    <span className="cat-preset-url">{p.base_url.replace(/^https?:\/\//, '')}</span>
                  </span>
                  {p.installed.length > 0 && <span className="cat-preset-badge">{t(G.presets.installed, { n: p.installed.length })}</span>}
                </button>
              ))}
            </div>
          )}
          <div className="ap-row" style={{ justifyContent: 'flex-start' }}>
            <Button variant="ghost" size="sm" icon={<IconSettings size={14} />} onClick={onCustom}>
              {t(G.presets.custom)}
            </Button>
          </div>
        </div>
      ) : (
        <Form onSubmit={install} className="ap-form cat-preset-body">
          <div className="cat-preset-head">
            <PresetIcon p={picked} />
            <div className="cat-preset-main">
              <span className="cat-preset-url">{baseUrl.replace(/^https?:\/\//, '')}</span>
              <span className="cat-preset-url">{t(G.presets.protocol, { p: picked.provider_type })}</span>
            </div>
            {picked.docs_url && (
              <a className="cat-preset-docs" href={picked.docs_url} target="_blank" rel="noreferrer">
                {t(G.presets.docs)}
                <IconExternalLink size={12} />
              </a>
            )}
          </div>
          {error && <Notice tone="danger">{error}</Notice>}
          <Field label={t(G.presets.keyLabel)} hint={t(G.presets.keyHint)}>
            <Input
              mono
              type="text"
              autoComplete="off"
              spellCheck={false}
              autoFocus
              required
              minLength={8}
              maxLength={4096}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </Field>
          <div className="ap-grid2">
            <Field label={t(G.presets.idLabel)} hint={t(G.presets.idHint)}>
              <Input mono required pattern="[a-z0-9][a-z0-9_\-]{1,62}" value={id} onChange={(e) => setId(e.target.value)} />
            </Field>
            <Field label={t(G.presets.nameLabel)}>
              <Input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          {advanced ? (
            <Field label={t(G.providers.baseTitle)}>
              <Input mono required maxLength={500} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
            </Field>
          ) : (
            <button type="button" className="cat-preset-link" onClick={() => setAdvanced(true)}>
              {t(G.presets.advanced)}
            </button>
          )}
          {presetFlags(picked).length > 0 && (
            <div className="ap-muted" style={{ fontSize: 12 }}>
              {t(G.presets.presetOptions, { list: presetFlags(picked).join(locale === 'zh' ? '，' : ', ') })}
            </div>
          )}
          <div className="ap-row ap-row--between">
            <Button variant="ghost" icon={<IconArrowLeft size={14} />} disabled={busy} onClick={() => setPicked(null)}>
              {t(G.presets.back)}
            </Button>
            <div className="ap-row">
              <Button onClick={close} disabled={busy}>
                {t(C.cancel)}
              </Button>
              <Button type="submit" variant="primary" loading={busy}>
                {busy ? t(G.presets.installing) : t(G.presets.install)}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Modal>
  )
}
